// Diagnostic output for the map layers feature.
//
// THIS MODULE IS THE ONLY PLACE UNDER src/components/mapLayers WHERE `console`
// MAY BE NAMED. Everything else in the feature routes through the functions
// below. The reason is a merge gate rather than taste: CodeRabbit's Production
// Readiness check fails any PR that adds a bare `console.*`, and its remedy line
// asks for "an approved logging mechanism". There was none in this repo, so the
// rule was unsatisfiable. This is that mechanism, deliberately kept to three
// functions rather than a logging framework, because nothing here needs levels,
// transports or buffering.
//
// The rest of the app still holds roughly 450 `console.*` calls. Migrating them
// is future work, not a precondition for this module; it is available to them.

/* eslint-disable no-console -- see the module comment: this is the one exception. */

/**
 * Read fresh on every call rather than captured once. Jest sets NODE_ENV to
 * 'test', so a test that wants to see the dev behaviour flips the variable and
 * restores it; a captured constant would make that untestable. In a CRA build
 * the comparison is inlined and the call collapses to a no-op either way.
 */
function isDeveloperBuild() {
  return process.env.NODE_ENV === 'development';
}

/**
 * A developer diagnostic for something that failed. Silent outside a developer
 * build: since BBB-527 wired the vector layers into the health tracker, a failed
 * layer publishes `unavailable` and the farmer is told "Not available right
 * now", so nothing user-facing depends on this reaching a console.
 */
export function reportError(...args) {
  if (isDeveloperBuild()) console.error(...args);
}

/** As reportError, for something suspect that did not stop the work. */
export function reportWarn(...args) {
  if (isDeveloperBuild()) console.warn(...args);
}

/**
 * A collapsed group wrapping one table, for the metrics harness.
 *
 * Unlike the two above this is NOT gated on NODE_ENV, and that asymmetry is
 * deliberate. The harness exists to measure a deployed environment with real
 * network latency (it is what timed the zone-open freeze at 10,168 ms and
 * confirmed the fix at 132 ms), so tying it to a development build would delete
 * the only thing it is for. It carries its own gate instead: an explicit,
 * off-by-default runtime opt-in that a developer sets by hand on the device in
 * front of them. See isMetricsEnabled in metrics.js.
 *
 * ANY OTHER CALLER MUST CARRY A GATE OF ITS OWN. Reach for reportWarn or
 * reportError unless you have a deliberate off-by-default switch like that one.
 *
 * groupCollapsed, table and groupEnd are all optional-called because they are
 * not in every console implementation, jsdom's included.
 *
 * @param {object} args
 * @param {string} args.headline summary line, shown on the collapsed group
 * @param {object[]} args.rows one object per table row
 * @param {string} [args.note] extra line inside the group, when there is one
 */
export function reportGroup({ headline, rows, note }) {
  console.groupCollapsed?.(headline);
  console.table?.(rows);
  if (note) console.warn(note);
  console.groupEnd?.();
}
